import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Wake-word hook using the Web Speech API.
 *
 * - Continuously listens (single-shot restart on end/error, cheap on quota).
 * - When wakePhrase appears in interim/final transcript, flips to "capturing"
 *   and collects the follow-on speech for ~5s of silence, then fires onQuestion.
 * - No audio ever leaves the browser during wake detection — only after the
 *   full question is transcribed do we hand it to the backend.
 *
 * Returns { state, error, enabled, enable, disable, lastHeard }
 *   state: 'off' | 'listening' | 'capturing' | 'error'
 */
export function useWakeWord({ wakePhrase = "hey sally", enabled, onQuestion }) {
  const [state, setState] = useState(enabled ? "listening" : "off");
  const [error, setError] = useState(null);
  const [lastHeard, setLastHeard] = useState("");
  const recRef = useRef(null);
  const stoppedRef = useRef(false);
  const bufferRef = useRef("");
  const silenceTimer = useRef(null);
  const wakeLc = wakePhrase.toLowerCase();

  const stopRec = useCallback(() => {
    stoppedRef.current = true;
    if (recRef.current) {
      try { recRef.current.stop(); } catch { /* ignore */ }
    }
    if (silenceTimer.current) {
      clearTimeout(silenceTimer.current);
      silenceTimer.current = null;
    }
    setState("off");
  }, []);

  const startRec = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setError("Speech recognition not supported in this browser");
      setState("error");
      return;
    }
    stoppedRef.current = false;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";

    rec.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        interim += t + " ";
      }
      const lower = interim.toLowerCase();
      setLastHeard(interim.slice(-90));

      if (state !== "capturing" && lower.includes(wakeLc)) {
        // Wake fired — cut the phrase and start capturing what's after
        const after = lower.split(wakeLc).slice(1).join(wakeLc).trim();
        bufferRef.current = after;
        setState("capturing");
        return;
      }

      if (state === "capturing") {
        bufferRef.current = interim.trim();
        // reset silence timer — 1.6s of no new tokens flushes the question
        if (silenceTimer.current) clearTimeout(silenceTimer.current);
        silenceTimer.current = setTimeout(() => {
          const q = bufferRef.current.trim();
          bufferRef.current = "";
          setState("listening");
          if (q.length >= 2) onQuestion?.(q);
        }, 1600);
      }
    };

    rec.onerror = (e) => {
      // 'no-speech' or 'aborted' are routine — just restart
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setError("Microphone permission denied");
        setState("error");
        stoppedRef.current = true;
      }
    };

    rec.onend = () => {
      if (!stoppedRef.current) {
        // Auto-restart continuous listening
        try { rec.start(); } catch { /* recover on next tick */
          setTimeout(() => { try { rec.start(); } catch { /* give up */ } }, 400);
        }
      }
    };

    try {
      rec.start();
      recRef.current = rec;
      setState("listening");
      setError(null);
    } catch (e) {
      setError(e?.message || "Could not start recognition");
      setState("error");
    }
  }, [wakeLc, onQuestion, state]);

  useEffect(() => {
    if (enabled) startRec();
    else stopRec();
    return () => stopRec();
    // eslint-disable-next-line
  }, [enabled]);

  return { state, error, lastHeard, enable: startRec, disable: stopRec };
}
